import { Injectable } from '@angular/core';
import { CaqRequestService, LocalizacaoService, CondicaoQualidadeStateService } from '../';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';
import { AcessoOfertaEducacionalStateService } from '../acesso-oferta-educacional-state/acesso-oferta-educacional-state.service';
import { PrecoEncargosAdicionaisStateService } from '../preco-encargos-adicionais-state/preco-encargos-adicionais-state.service';
import { firstValueFrom, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  MemoriaDeCalculoDto,
  PQRAdicionaisDto,
  PQRAlimentacaoEscolarDto,
  PQRCargaHorarioEnsinoDto,
  PQREncargosSociaisDto,
  PQRFormacaoJornadaRemuneracaoProfessoresDto,
  PQRManutencaoFormacaoMaterialDto,
  PQRNumeroDiasLetivosDto,
  PQRNovasSalasPrediosDto,
  PQRQuadroFuncionariosRemuneracaoDto,
  PQRTamanhoTurmaDto,
  PqrQuantidadeNovasConstrucoesDto,
  ProjecaoOfertaMatriculas, PQRFatorPonderacaoDto,
  PQRMultiplicadorAnualDto
} from "../../dtos";
import { TipoSimulacao } from '../../enums/simulation-type.enum';
import { PQRTamanhoMinimoTurmaDto } from '../../dtos/pqr-tamanho-minimo-turma.dto';

interface MemoriaDeCalculoResponse {
  content: MemoriaDeCalculoDto;
}

export interface MemoriaDeCalculoRequestDto {
  diagnosticoDeMatriculaPorEscola: ProjecaoOfertaMatriculas[];
  pqrNumeroDiasLetivosList: PQRNumeroDiasLetivosDto[];
  pqrCargaHorariaEnsinosList: PQRCargaHorarioEnsinoDto[];
  pqrTamanhoTurmaList: PQRTamanhoTurmaDto[];
  pqrTamanhoMinimoTurmaList?: PQRTamanhoMinimoTurmaDto[];
  pqrFormacaoJornadaRemuneracaoProfessoresList: PQRFormacaoJornadaRemuneracaoProfessoresDto[];
  pqrQuadroFuncionariosRemuneracaoList: PQRQuadroFuncionariosRemuneracaoDto[];
  pqrManutencaoFormacaoMaterialList: PQRManutencaoFormacaoMaterialDto[];
  pqrAlimentacaoEscolarList: PQRAlimentacaoEscolarDto[];
  pqrEncargosSociaisList: PQREncargosSociaisDto[];
  pqrMultiplicadorAnualList?: PQRMultiplicadorAnualDto[];
  pqrAdicionaisList: any[];
  pqrNovasSalasPredioList?: PQRNovasSalasPrediosDto[];
  overridesNovasConstrucoes?: PqrQuantidadeNovasConstrucoesDto[];
  pqrFatorPonderacaoList: PQRFatorPonderacaoDto[];
}

@Injectable({
  providedIn: 'root'
})
export class MemoriaDeCalculoService {
  public urlBase = `${environment.apiUrl}/simulador/memoria`;

  constructor(
    private caqRequestService: CaqRequestService,
    private localizacaoService: LocalizacaoService,
    // Injeção dos StateServices
    private condicaoQualidadeStateService: CondicaoQualidadeStateService,
    private acessoOfertaEducacionalStateService: AcessoOfertaEducacionalStateService,
    private precoEncargosAdicionaisStateService: PrecoEncargosAdicionaisStateService,
    private featureFlagService: FeatureFlagService
  ) { }

  // Mantido para compatibilidade com outros componentes que utilizam esse método
  async buscarMemoriaDeCalculo(): Promise<MemoriaDeCalculoDto> {
    return firstValueFrom(this.caqRequestService
      .get<MemoriaDeCalculoResponse>(this.urlBase)
      .pipe(map((response) => response.content)));
  }

  async buscarMemoriaDeCalculoLocalizacao(): Promise<MemoriaDeCalculoDto> {
    const url = this.construirUrlMemoriaCalculo(this.localizacaoService);

    return firstValueFrom(this.caqRequestService
      .get<MemoriaDeCalculoDto>(url)
      .pipe(map((response) => {

        return response

      })));
  }

  // Buscar dados das escolas/instituições individualmente
  async buscarMemoriaDeCalculoPorInstituicao(codInstituicao: number): Promise<MemoriaDeCalculoDto> {
    const url = `${this.urlBase}/instituicao/${codInstituicao}`;

    return await firstValueFrom(
      this.caqRequestService.get<MemoriaDeCalculoDto>(url)
    );
  }

  async recalcularMemoriaDeCalculo(): Promise<MemoriaDeCalculoDto> {
    try {
      const requisicao = await this.criarRequisicao(this.obterValoresEditados());

      // Constrói URL para POST baseada na localização
      const url = this.construirUrlMemoriaCalculo(this.localizacaoService);

      const resultado = await firstValueFrom(this.caqRequestService
        .post<MemoriaDeCalculoResponse>(url, requisicao)
        .pipe(map((response) => {
          // Verifica se a resposta é válida
          const data = response.content || response;
          if (Array.isArray(data) && data.length === 0) {
            throw new Error('Backend retornou array vazio');
          }

          if (!data || typeof data !== 'object') {
            throw new Error('Backend retornou dados inválidos');
          }

          return data;
        })));

      return resultado;
    } catch (error: any) {
      return await this.buscarMemoriaDeCalculoLocalizacao();
    }
  }

  // Recalcula a memória de uma escola com os PQRs editados (matrículas da escola não são editáveis)
  async recalcularMemoriaDeCalculoPorInstituicao(codInstituicao: number): Promise<MemoriaDeCalculoDto> {
    const requisicao = this.criarRequisicao(this.obterValoresEditados());
    delete requisicao.diagnosticoDeMatriculaPorEscola;

    return firstValueFrom(
      this.caqRequestService.post<MemoriaDeCalculoDto>(`${this.urlBase}/instituicao/${codInstituicao}`, requisicao)
    );
  }

  private obterValoresEditados(): MemoriaDeCalculoRequestDto {
    return {
      diagnosticoDeMatriculaPorEscola: this.acessoOfertaEducacionalStateService.getProjecoesOfertaMatriculasValores(),
      pqrNumeroDiasLetivosList: this.condicaoQualidadeStateService.getPQRNumeroDiasLetivosValores(),
      pqrCargaHorariaEnsinosList: this.condicaoQualidadeStateService.getPQRCargaHorarioEnsinoValores(),
      pqrTamanhoTurmaList: this.condicaoQualidadeStateService.getPQRTamanhoTurmasValores(),
      pqrTamanhoMinimoTurmaList: this.featureFlagService.funcionalidadeHabilitada('1104-creche-idade') && this.featureFlagService.funcionalidadeHabilitada('1108-pqr-minimo-turma')
        ? this.condicaoQualidadeStateService.getPQRMinimoTurmasValores()
        : undefined,
      pqrFormacaoJornadaRemuneracaoProfessoresList: this.condicaoQualidadeStateService.getPQRFormacaoJornadaRemuneracaoProfessoresValores(),
      pqrQuadroFuncionariosRemuneracaoList: this.condicaoQualidadeStateService.getPQRQuadroFuncionariosRemuneracaoValores(),
      pqrManutencaoFormacaoMaterialList: this.condicaoQualidadeStateService.getPQRManutencaoFormacaoMaterialValores(),
      pqrAlimentacaoEscolarList: this.condicaoQualidadeStateService.getPQRAlimentacaoEscolarValores(),
      pqrEncargosSociaisList: this.precoEncargosAdicionaisStateService.getPQREncargosSociaisValores(),
      pqrMultiplicadorAnualList: this.precoEncargosAdicionaisStateService.getPQRMultiplicadorAnualValores(),
      pqrAdicionaisList: this.precoEncargosAdicionaisStateService.getPQRAdicionaisValores(),
      pqrNovasSalasPredioList: this.condicaoQualidadeStateService.getPQRNovasSalasPrediosPadrao(),
      overridesNovasConstrucoes: this.condicaoQualidadeStateService.getPqrQuantidadeNovasConstrucoesValores(),
      pqrFatorPonderacaoList: this.condicaoQualidadeStateService.getPQRFatorPonderacaoValores()
    };
  }

  async recalcularMemoriaDeCalculoComNovosPqrs(): Promise<MemoriaDeCalculoDto> {
    return firstValueFrom(this.caqRequestService
      .post<MemoriaDeCalculoResponse>(`${this.urlBase}/nacional/recalcular`, {})
      .pipe(map((response) => response.content)));
  }

  // PDF por escola da tabela "Salas de aula" (#1472). Sem edições envia corpo nulo e o backend usa a simulação padrão.
  async baixarPdfSalasDeAula(comEdicao: boolean, codInstituicao?: number): Promise<Blob> {
    const url = codInstituicao
      ? `${this.urlBase}/instituicao/${codInstituicao}`
      : this.construirUrlMemoriaCalculo(this.localizacaoService);
    const requisicao = comEdicao ? this.criarRequisicao(this.obterValoresEditados()) : null;
    if (requisicao && codInstituicao) {
      // Mesmo corpo de recalcularMemoriaDeCalculoPorInstituicao: matrículas da escola não são editáveis
      delete requisicao.diagnosticoDeMatriculaPorEscola;
    }

    return firstValueFrom(this.caqRequestService.postBlob(`${url}/salas-de-aula/pdf`, requisicao));
  }

  /**
 * Monta a requisição no formato esperado pelo backend (RequisicaoMemoriaCalculoDto).
 * Para níveis estadual/nacional, não inclui diagnosticoDeMatriculaPorEscola.
 */
  private criarRequisicao(memoriaDeCalculo: MemoriaDeCalculoRequestDto): any {
    const ehMunicipal = this.localizacaoService.ehLocalizacaoMunicipal();

    // Obtém os PQRs padrão de novas salas (necessário para o backend construir a TabelaPQRNovasSalasPredios)
    let pqrNovasSalasPredioList = memoriaDeCalculo.pqrNovasSalasPredioList ||
      this.condicaoQualidadeStateService.getPQRNovasSalasPrediosPadrao();

    // Incorpora os overrides dentro dos PQRs (seguindo padrão dos outros PQRs)
    if (memoriaDeCalculo.overridesNovasConstrucoes && memoriaDeCalculo.overridesNovasConstrucoes.length > 0) {
      // Cria uma cópia dos PQRs e adiciona as quantidades editadas
      pqrNovasSalasPredioList = pqrNovasSalasPredioList.map(pqr => {
        const pqrComOverride = { ...pqr };

        // Busca overrides para este PQR
        const capacidade = this.extrairCapacidadePorId(pqr.codPqrNovasSalasPredios);
        const overridesParaEstePqr = memoriaDeCalculo.overridesNovasConstrucoes!.filter(
          ov => ov.tamanhoPorNumeroSala === capacidade
        );

        // Adiciona as quantidades editadas ao objeto PQR
        overridesParaEstePqr.forEach(ov => {
          if (ov.pqrEducacaoInfantil) {
            if (ov.areaLocalidade === 'Urbana') {
              (pqrComOverride as any).quantidadeUrbanaEducacaoInfantil = ov.quantidade;
            } else if (ov.areaLocalidade === 'Rural') {
              (pqrComOverride as any).quantidadeRuralEducacaoInfantil = ov.quantidade;
            }
          } else {
            if (ov.areaLocalidade === 'Urbana') {
              (pqrComOverride as any).quantidadeUrbanaEnsinoFundamentalMedioEja = ov.quantidade;
            } else if (ov.areaLocalidade === 'Rural') {
              (pqrComOverride as any).quantidadeRuralEnsinoFundamentalMedioEja = ov.quantidade;
            }
          }
        });

        return pqrComOverride;
      });
    }

    const requisicao: any = {
      pqrNumeroDiasLetivosList: memoriaDeCalculo.pqrNumeroDiasLetivosList,
      pqrCargaHorariaEnsinosList: memoriaDeCalculo.pqrCargaHorariaEnsinosList,
      pqrTamanhoTurmaList: memoriaDeCalculo.pqrTamanhoTurmaList,
      ...(this.featureFlagService.funcionalidadeHabilitada('1104-creche-idade') && this.featureFlagService.funcionalidadeHabilitada('1108-pqr-minimo-turma')
        ? { pqrTamanhoMinimoTurmaList: memoriaDeCalculo.pqrTamanhoMinimoTurmaList }
        : {}),
      pqrFormacaoJornadaRemuneracaoProfessoresList: memoriaDeCalculo.pqrFormacaoJornadaRemuneracaoProfessoresList,
      pqrQuadroFuncionariosRemuneracaoList: memoriaDeCalculo.pqrQuadroFuncionariosRemuneracaoList,
      pqrManutencaoFormacaoMaterialList: memoriaDeCalculo.pqrManutencaoFormacaoMaterialList,
      pqrAlimentacaoEscolarList: memoriaDeCalculo.pqrAlimentacaoEscolarList,
      pqrEncargosSociaisList: memoriaDeCalculo.pqrEncargosSociaisList,
      pqrMultiplicadorAnualList: memoriaDeCalculo.pqrMultiplicadorAnualList,
      pqrAdicionaisList: memoriaDeCalculo.pqrAdicionaisList,
      pqrNovasSalasPredioList: pqrNovasSalasPredioList,
      pqrFatorPonderacaoList: memoriaDeCalculo.pqrFatorPonderacaoList
    };

    if (ehMunicipal) {
      requisicao.diagnosticoDeMatriculaPorEscola = memoriaDeCalculo.diagnosticoDeMatriculaPorEscola;
    }

    return requisicao;
  }

  private extrairCapacidadePorId(codPqr: number): number {
    // Mapeia o ID do PQR para a capacidade (número de salas)
    const capacidadeMap: Record<number, number> = {
      1: 1,
      2: 2,
      3: 4,
      4: 6,
      5: 12,
      6: 16,
      7: 24,
      8: 32
    };
    return capacidadeMap[codPqr] || 0;
  }

  private construirUrlMemoriaCalculo(localizacaoService: LocalizacaoService): string {
    const localizacao = localizacaoService.obterLocalizacaoSelecionada();

    if (!localizacao) {
      return `${this.urlBase}/nacional`;
    }

    const tipo = localizacao.tipoSimulacaoSelecionado;

    if (tipo === TipoSimulacao.planejamentoNacional) {
      return `${this.urlBase}/nacional`;
    }

    if (localizacao.cidadeSelecionada) {
      const codMun = localizacao.cidadeSelecionada.value;

      if (tipo === TipoSimulacao.planejamento) {
        return `${this.urlBase}/plano/municipio/${codMun}`;
      } else if (tipo === TipoSimulacao.planejamentoPorEsferaAdmMunicipal) {
        return `${this.urlBase}/rede/municipio/${codMun}`;
      }
    }

    if (localizacao.estadoSelecionado) {
      const codUf = localizacao.estadoSelecionado.value;

      if (tipo === TipoSimulacao.planejamento) {
        return `${this.urlBase}/plano/estado/${codUf}`;
      } else if (tipo === TipoSimulacao.planejamentoPorEsferaAdmEstadual) {
        return `${this.urlBase}/rede/estado/${codUf}`;
      }
    }

    throw new Error('Tipo de simulação não identificado ou localização inválida');
  }

}
